import { takeLatest, call, put, select } from 'redux-saga/effects';
import { PowertrainApiV2 } from 'api';
import { apiCallStatus } from 'helpers/constants';
import logger from 'helpers/logger';
import { translate } from 'helpers/translateHelper';
import { notify } from 'sharedComponents/GeneralNotification';
import { NOTIFICATION_TYPES } from 'sharedComponents/GeneralNotification/constants';
import { actionTypes } from './constants';
import {
    getAvailableAssetsAction,
    getAssetDetailsAction,
    getAssetSubTypesAction
} from './actions';
import {
    formatAddPowertrainRequest,
    formatEditPowertrainRequest,
    formatGetAssetDetailsRequest,
    formatGetAssetsRequest
} from './helpers';
import {
    reloadOverviewPowertrainsRequest,
    getPowertrainsRequest,
    setSelectedPowertrain,
    setSelectedHierarchyNodes,
    getHierarchyDataRequest
} from '../actions';
import { setPowertrainUpdateStatus } from '../../../PowertrainConditionMonitoring/ConditionIndicesOverviewV2/actions';

function* addPowertrainSaga({ powertrain }) {
    try {
        const response = yield call(PowertrainApiV2.addPowertrain, formatAddPowertrainRequest(powertrain));

        if (response && response.success && response.powertrain) {
            // Success case - powertrain was created successfully
            yield put({ type: actionTypes.ADD_POWERTRAIN_SUCCESS, payload: response.powertrain });

            notify(translate('ABB.Powertrain.Frontend.powertrainWizardPowertrainAddSuccessMessage'), NOTIFICATION_TYPES.SUCCESS);
            logger.debug('Powertrain successfully added');

            // Reload overview powertrains
            yield put(reloadOverviewPowertrainsRequest());

            // Get current state to refresh the powertrains list with current filters
            const state = yield select();
            const searchboxFilter = state?.powertrainTools?.configurationV2?.searchboxFilter || '';
            const selectedSites = state?.generalFilter?.selectedFilters?.sites || [];
            const organizations = state?.generalFilter?.selectedFilters?.organizations || [];
            const siteIds = selectedSites.map(site => site.siteID);
            const organizationIds = organizations.map(org => org.organizationID);

            // Refresh the powertrains list to include the newly created powertrain
            if (siteIds?.length > 0 || organizationIds?.length > 0) {
                yield put(getPowertrainsRequest(siteIds, organizationIds, searchboxFilter, 1));
            }

            // Select the newly created powertrain after the list is refreshed
            yield put(setSelectedPowertrain(response?.powertrain));
            yield put(setSelectedHierarchyNodes([response.powertrain.powertrainId]));
            const organizationID = organizations?.[0]?.organizationID;
            yield put(getHierarchyDataRequest(organizationID, siteIds));
            yield put(setPowertrainUpdateStatus(true));
        } else {
            // API returned success: false or missing powertrain object
            const errorMessage = response?.validationMessages?.length > 0
                ? response.validationMessages.join(', ')
                : translate('ABB.Powertrain.Frontend.powertrainWizardPowertrainAddErrorMessage');

            yield put({
                type: actionTypes.ADD_POWERTRAIN_FAILURE,
                payload: { message: errorMessage },
                error: true
            });
            notify(errorMessage, NOTIFICATION_TYPES.ERROR);
            logger.error('Failed to add powertrain:', errorMessage);
        }
    } catch (err) {
        // Network or other errors
        const errorMessage = err?.message || translate('ABB.Powertrain.Frontend.powertrainWizardPowertrainAddErrorMessage');

        yield put({
            type: actionTypes.ADD_POWERTRAIN_FAILURE,
            payload: { message: errorMessage },
            error: true
        });
        notify(errorMessage, NOTIFICATION_TYPES.ERROR);
        logger.error('Error in addPowertrainSaga:', err);
    }
}

function* editPowertrainSaga({ powertrain }) {
    // Don't dispatch EDIT_POWERTRAIN_REQUEST again - it would trigger the saga again!
    // The loading state is already managed when the action was originally dispatched

    try {
        const formattedPayload = formatEditPowertrainRequest(powertrain);

        const response = yield call(PowertrainApiV2.updatePowertrain, formattedPayload);

        // Validate API response
        if (response && response.success) {
            // Success case - powertrain was updated successfully
            // Update selected powertrain with response data if available
            const updatedPowertrain = response.powertrain || {
                ...powertrain,
                // Ensure we have the latest rowVersion from response if provided
                rowVersion: response.rowVersion || powertrain.rowVersion
            };

            // Dispatch EDIT_POWERTRAIN_SUCCESS for wizard component
            yield put({
                type: actionTypes.EDIT_POWERTRAIN_SUCCESS,
                payload: {
                    powertrain: updatedPowertrain,
                    powertrainId: powertrain.powertrainId
                }
            });

            // Show success notification to user
            notify(translate('ABB.Powertrain.Frontend.powertrainWizardPowertrainUpdateSuccessMessage'), NOTIFICATION_TYPES.SUCCESS);
            logger.debug('Powertrain successfully updated');

            // Reload both overview and configuration lists to show updated data
            yield put(reloadOverviewPowertrainsRequest());
            yield put(setPowertrainUpdateStatus(true));

            // Reload configuration powertrains list with current filters
            const state = yield select();
            const searchboxFilter = state.powertrainTools?.configurationV2?.searchboxFilter;
            const selectedSites = state.generalFilter?.selectedFilters?.sites || [];
            const organizations = state.generalFilter?.selectedFilters?.organizations || [];
            const siteIds = selectedSites.map(site => site.siteID);
            const organizationIds = organizations.map(org => org.organizationID);

            if (siteIds.length > 0 || organizationIds.length > 0) {
                yield put(getPowertrainsRequest(siteIds, organizationIds, searchboxFilter, 1));
            }

            // Update the selected powertrain with the latest data from the response
            // This ensures the details panel shows the updated information
            if (response?.powertrain) {
                yield put(setSelectedPowertrain(response.powertrain));
                yield put(setSelectedHierarchyNodes([response.powertrain.powertrainId]));
            } else {
                // If API didn't return the full powertrain object, update with the data we sent
                yield put(setSelectedPowertrain(updatedPowertrain));
                yield put(setSelectedHierarchyNodes([updatedPowertrain.powertrainId]));
            }
            const organizationID = organizations?.[0]?.organizationID;
            yield put(getHierarchyDataRequest(organizationID, siteIds));
        } else {
            // API returned success: false or validation errors
            const errorMessage = response?.validationMessages?.length > 0
                ? response.validationMessages.join(', ')
                : response?.message || translate('ABB.Powertrain.Frontend.powertrainWizardPowertrainUpdateErrorMessage');

            yield put({
                type: actionTypes.EDIT_POWERTRAIN_FAILURE,
                payload: { message: errorMessage },
                error: true
            });
            notify(errorMessage, NOTIFICATION_TYPES.ERROR);
            logger.error('Failed to update powertrain:', errorMessage);
        }
    }
    catch (err) {
        // Network or other errors
        const errorMessage = err?.message || translate('ABB.Powertrain.Frontend.powertrainWizardPowertrainUpdateErrorMessage');

        yield put({
            type: actionTypes.EDIT_POWERTRAIN_FAILURE,
            payload: { message: errorMessage },
            error: true
        });
        notify(errorMessage, NOTIFICATION_TYPES.ERROR);
        logger.error('Error in editPowertrainSaga:', err);
    }
}

function* getAvailableAssetsSaga({ siteId, pageNumber, pageSize }) {
    yield put(getAvailableAssetsAction(apiCallStatus.LOADING));
    try {
        const assets = yield call(PowertrainApiV2.getAvailableAssets, formatGetAssetsRequest(siteId, pageNumber, pageSize));
        yield put(getAvailableAssetsAction(apiCallStatus.SUCCESS, {
            assets, pageNumber
        }));
        logger.debug('Available assets successfully loaded');
    }
    catch (err) {
        yield put(getAvailableAssetsAction(apiCallStatus.ERROR, [], err));
    }
}
function* getAssetDetailsSaga({ assets }) {
    try {
        const assetDetails = yield call(PowertrainApiV2.getAssetDetails, formatGetAssetDetailsRequest(assets));
        yield put(getAssetDetailsAction(apiCallStatus.SUCCESS, assetDetails));
    }
    catch (err) {
        yield put(getAssetDetailsAction(apiCallStatus.ERROR, [], err));
    }
}

function* getAssetSubTypesSaga({ typeConfigId }) {
    yield put(getAssetSubTypesAction(apiCallStatus.LOADING));
    try {
        const assetSubTypes = yield call(PowertrainApiV2.getAssetSubTypes, typeConfigId);
        yield put(getAssetSubTypesAction(apiCallStatus.SUCCESS, assetSubTypes));
        logger.debug('Asset subtypes successfully loaded');
    }
    catch (err) {
        yield put(getAssetSubTypesAction(apiCallStatus.ERROR, [], err));
        logger.error('Failed to load asset subtypes', err);
    }
}

export function* powertrainWizardWatchers() {
    yield takeLatest(actionTypes.ADD_POWERTRAIN_REQUEST, addPowertrainSaga);
    yield takeLatest(actionTypes.EDIT_POWERTRAIN_REQUEST, editPowertrainSaga);
    yield takeLatest(actionTypes.GET_AVAILABLE_ASSETS_REQUEST, getAvailableAssetsSaga);
    yield takeLatest(actionTypes.GET_ASSET_DETAILS_REQUEST, getAssetDetailsSaga);
    yield takeLatest(actionTypes.GET_ASSET_SUB_TYPES_REQUEST, getAssetSubTypesSaga);
}
